import { ContentStatuses } from '@sparted/shared-library/business/types';
import { LanguageCode } from '@sparted/shared-library/business/types/multilingual';
import { t } from 'i18next';

import { TRANSLATION_STATUS_TO_STATUS_TYPE_MAP } from 'Components/foundation/icons';
import { GET_TRANSLATION_STATUS_LABEL_MAP } from 'Components/foundation/translations';
import { BUTTON_TYPES } from 'Components/unit/UButton/UButton.utils';
import { getLanguageFromCode } from 'Services/language/languageService';
import { ROLES, Role } from 'Services/role/roleService';
import { ContentEntity } from 'Store/entities/content/content.types';

type GetButtonStateParams = {
  disabled?: boolean;
  isFormDirty: boolean;
  contentStatus: ContentStatuses[keyof ContentStatuses];
  roles: Role[];
  onSave: () => void;
  onValidate: () => void;
  onUpdateValidation: () => void;
  onUnValidate: () => void;
  onAskValidation: () => void;
  onSaveTranslation: () => void;
};

const isAdmin = (roles: Role[]) =>
  roles.some((role) => role.name === ROLES.ADMIN || role.name === ROLES.ROOT || role.name === ROLES.MASTER_ADMIN);

const isWriter = (roles: Role[]) => roles.some((role) => role.name === ROLES.WRITER);

type getAvailableLanguagesFromStatusParams = {
  statuses?: ContentEntity['statuses'];
  setLanguage: (language: LanguageCode) => void;
};

export const getAvailableLanguagesFromStatus = ({ statuses, setLanguage }: getAvailableLanguagesFromStatusParams) => {
  if (!statuses) return [];
  return Object.entries(statuses).map(([language, status]) => {
    const { languageName } = getLanguageFromCode(language as LanguageCode);
    const validatedLanguages = Object.keys(statuses).filter(
      (languageCode) => statuses[languageCode as LanguageCode] === 'validated',
    );
    const defaultLanguage = validatedLanguages?.length === 1 ? validatedLanguages[0] : null;

    return {
      text: languageName,
      languageCode: language as LanguageCode,
      isDefault: defaultLanguage ? defaultLanguage === language : false,
      status: {
        type: TRANSLATION_STATUS_TO_STATUS_TYPE_MAP[status],
        text: GET_TRANSLATION_STATUS_LABEL_MAP()[status],
      },
      onClick: (language: LanguageCode) => {
        setLanguage(language);
      },
    };
  });
};

export const getButtonsState = ({
  disabled,
  isFormDirty,
  contentStatus,
  roles,
  onSave,
  onSaveTranslation,
  onAskValidation,
  onUnValidate,
  onUpdateValidation,
  onValidate,
}: GetButtonStateParams) => {
  if (!isAdmin(roles) && !isWriter(roles)) {
    return {
      saveButton: {
        disabled: true,
        text: t('content_translation:content_edition_bar.save'),
        onClick: () => {},
      },
      validateButton: {
        disabled: true,
        text: t('content_translation:content_edition_bar.validate'),
        onClick: () => {},
      },
    };
  }
  if (contentStatus === 'draft') {
    return {
      saveButton: {
        disabled: disabled || !isFormDirty,
        text: t('content_translation:content_edition_bar.save'),
        onClick: onSave,
      },
      validateButton: isAdmin(roles)
        ? {
            disabled: disabled || isFormDirty,
            text: t('content_translation:content_edition_bar.validate'),
            onClick: onValidate,
          }
        : {
            disabled: disabled || isFormDirty,
            text: t('content_translation:content_edition_bar.ask_validate'),
            onClick: onAskValidation,
          },
    };
  }

  if (contentStatus === 'validated') {
    if (isWriter(roles)) {
      return {
        saveButton: {
          disabled: true,
          text: t('content_translation:content_edition_bar.save'),
          onClick: () => {},
        },
        validateButton: {
          disabled: true,
          text: t('content_translation:content_edition_bar.validate'),
          onClick: () => {},
        },
      };
    }

    return {
      saveButton: {
        disabled: disabled || !isFormDirty,
        text: t('content_translation:content_edition_bar.update'),
        onClick: onUpdateValidation,
      },
      validateButton: {
        disabled: disabled || isFormDirty,
        text: t('content_translation:content_edition_bar.unvalidate'),
        onClick: onUnValidate,
      },
    };
  }

  if (contentStatus === 'waiting-validation') {
    if (isWriter(roles)) {
      return {
        saveButton: {
          disabled: true,
          text: t('content_translation:content_edition_bar.save'),
          onClick: () => {},
        },
        validateButton: {
          disabled: true,
          text: t('content_translation:content_edition_bar.validate'),
          onClick: () => {},
        },
      };
    }

    return {
      saveButton: {
        disabled: disabled || !isFormDirty,
        text: t('content_translation:content_edition_bar.save'),
        onClick: onSave,
      },
      validateButton: {
        disabled: disabled || isFormDirty,
        text: t('content_translation:content_edition_bar.validate'),
        onClick: onValidate,
      },
    };
  }

  if (contentStatus === 'waiting-translation') {
    return {
      saveButton: {
        disabled: disabled || !isFormDirty,
        text: t('content_translation:content_edition_bar.save_draft'),
        onClick: onSaveTranslation,
      },
      validateButton: {
        disabled: disabled || isFormDirty,
        type: isFormDirty ? BUTTON_TYPES.STANDARD : BUTTON_TYPES.ACCENTUATED,
        text: t('content_translation:content_edition_bar.validate_translation'),
        onClick: onValidate,
      },
    };
  }

  return {
    saveButton: {
      disabled: true,
      text: t('content_translation:content_edition_bar.save'),
      onClick: () => {},
    },
    validateButton: {
      disabled: true,
      text: t('content_translation:content_edition_bar.validate'),
      onClick: () => {},
    },
  };
};
